import * as React from 'react';
import { useUser } from '@modules/user';
import type { Comment, Entity } from '@modules/build-hub-comment/types';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import { decodeMessage, toEntities } from '@modules/build-hub-comment/utils/comment-parser';
import { getInitials } from '@modules/build-hub/utils/get-initials';

export function CommentViewer({comments}: {comments: Comment[]}) {
    const user = useUser();

    if (!comments?.length) {
        return <p className="text-sm text-gray-500">No comments yet.</p>;
    }

    return (
        <ul className="space-y-3">
            {comments.map((comment) => {
                const {
                    id,
                    message,
                    createdAt,
                    author,
                    mentions,
                    files,
                } = comment;

                const isMine = author.id === user.ID;
                const messageEntities = toEntities(mentions ?? [], files ?? []);

                return (
                    <li
                        key={id}
                        className={`flex items-start gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                    >
                        {/* Avatar on left if not mine */}
                        {!isMine && (
                            <Avatar size="square40" borderWidth="none">
                                <AvatarImage
                                    src={author.avatar ?? undefined}
                                    alt={author.name}
                                    fit="cover"
                                />
                                <AvatarFallback size="md">
                                    {author.name ? getInitials(author.name) : "?"}
                                </AvatarFallback>
                            </Avatar>
                        )}

                        {/* Bubble with top tail */}
                        <div
                            className={`relative max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                                isMine ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
                            }`}
                        >
                            {/* Tail */}
                            <span
                                className={`absolute top-3 ${
                                    isMine ? '-right-2' : '-left-2'
                                } w-0 h-0 border-y-[6px] border-y-transparent ${
                                    isMine
                                        ? 'border-l-[8px] border-l-gray-900'
                                        : 'border-r-[8px] border-r-gray-100'
                                }`}
                            />

                            {!isMine && (
                                <div className="mb-1 text-[11px] font-medium text-gray-500">
                                    {author.name}
                                </div>
                            )}
                            <div className="whitespace-pre-wrap">
                                {renderMessageWithMentions(message, messageEntities, isMine)}
                            </div>
                            {createdAt && (
                                <div
                                    className={`mt-1 text-[10px] ${isMine ? 'text-gray-300' : 'text-gray-500'}`}
                                >
                                    {new Date(createdAt).toLocaleString()}
                                </div>
                            )}
                        </div>

                        {/* Avatar on right if mine */}
                        {isMine && (
                            <Avatar size="square40" borderWidth="none">
                                <AvatarImage
                                    src={author.avatar ?? undefined}
                                    alt={author.name}
                                    fit="cover"
                                />
                                <AvatarFallback size="md">
                                    {author.name ? getInitials(author.name) : "?"}
                                </AvatarFallback>
                            </Avatar>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}


/**
 * Render a message with mentions styled in lighter blue.
 * Uses decodeMessage + entities so multi-word mentions render correctly.
 */
function renderMessageWithMentions(
    rawMessage: string,
    entities: Entity[],
    isMine: boolean
) {
    const decoded = decodeMessage(rawMessage, entities);
    const sortedEntities = [...entities].sort(
        (a, b) => b.label.length - a.label.length
    );

    let parts: (string | JSX.Element)[] = [decoded];

    for (const ent of sortedEntities) {
        const next: (string | JSX.Element)[] = [];
        const needle = `@${ent.label}`;

        parts.forEach((p, i) => {
            if (typeof p === "string") {
                const split = p.split(needle);
                split.forEach((chunk, j) => {
                    if (chunk) next.push(chunk);
                    if (j < split.length - 1) {
                        next.push(
                            <span
                                key={`${ent.type}:${ent.id}:${i}:${j}`}
                                style={{
                                    color: isMine ? "#60a5fa" : "#2563eb", // lighter blue if mine
                                    fontWeight: 500,
                                }}
                            >
                {needle}
              </span>
                        );
                    }
                });
            } else {
                next.push(p);
            }
        });

        parts = next;
    }

    return <>{parts}</>;
}

